import { c as _c } from "react/compiler-runtime";
// Third Party
import * as React from 'react';
import { Transition } from '@headlessui/react';
import assertNever from 'assert-never';
import cx from 'classnames';

// Utils
import useOnclickOutside from 'react-cool-onclickoutside';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircleExclamation, faCircleX, faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
const useMounted = () => {
  const $ = _c(2);
  const isMounted = React.useRef(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      isMounted.current = true;
      return () => {
        isMounted.current = false;
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  React.useEffect(t0, t1);
  return isMounted;
};

/**
 * useAsyncSafeSetTimeout is a safe wrapper around setTimeout to prevent memory leaks. It will only execute the callback if the component is still mountd, and it automatically clears the timer on unmount.
 *
 */
function useAsyncSafeSetTimeout() {
  const $ = _c(4);
  const isMounted = useMounted();
  const timeoutId = React.useRef(null);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  React.useEffect(t0, t1);
  let t2;
  if ($[2] !== isMounted) {
    t2 = (callback, delay) => {
      const checkMountBeforeExecution = () => {
        isMounted.current && callback();
      };
      timeoutId.current = window.setTimeout(checkMountBeforeExecution, delay);
    };
    $[2] = isMounted;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
export type NotificationType = 'error' | 'success' | 'warning';
export const NotificationCard: React.FC<{
  markForRemoval?: boolean;
  uniqueId: string;
  message?: string;
  timeout?: number;
  title: string;
  type: NotificationType;
  removeNotification: (notificationId: string) => void;
}> = t0 => {
  const $ = _c(39);
  const {
    uniqueId,
    markForRemoval,
    timeout,
    type,
    title,
    message,
    removeNotification
  } = t0;
  const asyncSafeSetTimeout = useAsyncSafeSetTimeout();
  const [show, setShow] = React.useState(true);
  let t1;
  if ($[0] !== removeNotification || $[1] !== uniqueId) {
    t1 = () => removeNotification(uniqueId);
    $[0] = removeNotification;
    $[1] = uniqueId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const onClickOutsideRef = useOnclickOutside(t1);
  let t2;
  let t3;
  if ($[3] !== markForRemoval) {
    t2 = () => {
      if (markForRemoval) {
        setShow(false);
      }
    };
    t3 = [markForRemoval];
    $[3] = markForRemoval;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  React.useEffect(t2, t3);
  let t4;
  if ($[6] !== asyncSafeSetTimeout || $[7] !== timeout) {
    t4 = () => {
      if (timeout) {
        asyncSafeSetTimeout(() => setShow(false), timeout);
      }
    };
    $[6] = asyncSafeSetTimeout;
    $[7] = timeout;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  React.useEffect(t4, t5);
  const t6 = type === "success";
  const t7 = type === "warning";
  const t8 = type === "error";
  let t9;
  if ($[10] !== t6 || $[11] !== t7 || $[12] !== t8) {
    t9 = cx("ring-1 ring-black ring-opacity-5 border-1 w-full max-w-sm bg-white rounded shadow-xl pointer-events-auto overflow-hidden", {
      "border-success-main": t6,
      "border-alert-main": t7,
      "border-error-main": t8
    });
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  bb0: switch (type) {
    case "success":
      {
        let t11;
        if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
          t11 = <FontAwesomeIcon data-id="ICON-faCircleCheck" icon={faCircleCheck} className="icon text-success-main" />;
          $[14] = t11;
        } else {
          t11 = $[14];
        }
        t10 = t11;
        break bb0;
      }
    case "warning":
      {
        let t11;
        if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
          t11 = <FontAwesomeIcon data-id="ICON-faTriangleExclamation" icon={faTriangleExclamation} className="icon text-alert-main" />;
          $[15] = t11;
        } else {
          t11 = $[15];
        }
        t10 = t11;
        break bb0;
      }
    case "error":
      {
        let t11;
        if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
          t11 = <FontAwesomeIcon data-id="ICON-faCircleExclamation" icon={faCircleExclamation} className="icon text-error-main" />;
          $[16] = t11;
        } else {
          t11 = $[16];
        }
        t10 = t11;
        break bb0;
      }
    default:
      {
        let t11;
        if ($[17] !== type) {
          t11 = assertNever(type);
          $[17] = type;
          $[18] = t11;
        } else {
          t11 = $[18];
        }
        t10 = t11;
      }
  }
  let t11;
  if ($[19] !== t10) {
    t11 = <div className="flex-shrink-0">{t10}</div>;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== title) {
    t12 = <p className="text-gray-dark text-sm font-medium">{title}</p>;
    $[21] = title;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== message) {
    t13 = message && <p className="mt-1 text-gray-main text-sm">{message}</p>;
    $[23] = message;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t12 || $[26] !== t13) {
    t14 = <div className="pt-0.5 flex-1 ml-3 w-0">{t12}{t13}</div>;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="flex flex-shrink-0 ml-4"><button className="inline-flex text-gray-main hover:text-gray-dark bg-white rounded-md" onClick={() => setShow(false)}><span className="sr-only">Close</span><FontAwesomeIcon data-id="ICON-faCircleX" icon={faCircleX} className="icon" size="lg" /></button></div>;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t11 || $[30] !== t14) {
    t16 = <div className="p-4"><div className="flex items-start">{t11}{t14}{t15}</div></div>;
    $[29] = t11;
    $[30] = t14;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== onClickOutsideRef || $[33] !== t16 || $[34] !== t9) {
    t17 = <div ref={onClickOutsideRef} className={t9}>{t16}</div>;
    $[32] = onClickOutsideRef;
    $[33] = t16;
    $[34] = t9;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== show || $[37] !== t17) {
    t18 = <Transition appear={true} show={show} as="div" className="w-full max-w-sm flex justify-center items-center md:justify-end" enter="transition-all ease-out duration-500 transform" enterFrom="opacity-0 translate-x-full" enterTo="opacity-100 translate-x-0" leave="transition-all duration-500 transform" leaveTo="opacity-0">{t17}</Transition>;
    $[36] = show;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  return t18;
};